/* eslint-disable @typescript-eslint/no-explicit-any */
import axios from "axios";
import { API_URLS } from "../../../../core/data/redux/apis/urls";
import { tokenService } from "../../../../core/utils/tokenService";

export type DisplayMode = "EMPLOYEES" | "DIRECT_REPORTING" | "ORG_UNITS_AND_POSITIONS";

export interface ManagerSelfServiceParams {
  display: DisplayMode;
  empId: string;
  query?: string;
}

export interface EmployeeCardItem {
  id: string;
  empId: string;
  fullName: string;
  firstName?: string;
  lastName?: string;
  designation: string;
  department: string;
  wing?: string;
  email: string;
  phone: string;
  reportingManager?: string;
  location?: string;
  dateOfJoining?: string;
  status: string;
  image?: string;
  grade?: string;
  positionId?: string;
  positionTitle?: string;
  orgUnitId?: string;
  orgUnitName?: string;
  raw: Record<string, any>;
}

export interface InternalHierarchyItem {
  id: string;
  orgUnitId?: string;
  orgUnitName?: string;
  positionId?: string;
  positionTitle?: string;
  holderEmpId?: string;
  holderName?: string;
  designation?: string;
  department?: string;
  status?: string;
  reportsTo?: string;
  level?: number;
  internalHierarchies?: InternalHierarchyItem[];
  raw?: Record<string, any>;
}

export interface OrgUnitPositionItem {
  id: string;
  orgUnitId: string;
  orgUnitName: string;
  positionId: string;
  positionTitle: string;
  holderEmpId: string;
  holderName: string;
  reportsToPosition?: string;
  status: string;
  internalHierarchies?: InternalHierarchyItem[];
  raw: Record<string, any>;
}

export interface ReportingLine {
  empId: string;
  name: string;
  designation: string;
  orgUnitID: string;
  orgUnitName: string;
}

export interface ManagerSelfServiceResult {
  success: boolean;
  message?: string;
  displayMode: DisplayMode;
  empId: string;
  query: string;
  totalCount: number;
  reportingLine: ReportingLine | null;
  employees: EmployeeCardItem[];
  orgUnits: OrgUnitPositionItem[];
  rawResponse: any;
}

/**
 * Format employee ID with 8-digit zero padding (e.g., "346" -> "00000346")
 */
export function padEmpId(empId?: string | number | null): string {
  if (empId === null || empId === undefined || empId === "") return "00000346";
  const str = String(empId).trim();
  if (/^\d+$/.test(str) && str.length < 8) {
    return str.padStart(8, "0");
  }
  return str;
}

/**
 * Retrieve the current logged-in employee ID from localStorage/session
 */
export function getLoggedInEmpId(): string {
  try {
    const rawUser = localStorage.getItem("user") || sessionStorage.getItem("user");
    if (rawUser) {
      const user = JSON.parse(rawUser);
      const id = user?.empId || user?.details?.empId || user?.employee_id || user?.usrid || user?.id;
      if (id) return padEmpId(id);
    }
  } catch (e) {
    console.error("Error reading user from storage:", e);
  }
  return "00000346"; // Default fallback
}

/**
 * Generate a deterministic modern avatar color based on name/ID
 */
export function getAvatarDetails(name: string, empId?: string): { bg: string; text: string; initial: string } {
  const colors = [
    { bg: "#EBF3FF", text: "#1A56DB" },
    { bg: "#FEF2F2", text: "#DC2626" },
    { bg: "#F0FDF4", text: "#16A34A" },
    { bg: "#FAF5FF", text: "#9333EA" },
    { bg: "#FFF7ED", text: "#EA580C" },
    { bg: "#ECFEFF", text: "#0891B2" },
    { bg: "#FDF2F8", text: "#DB2777" },
    { bg: "#F1F5F9", text: "#475569" },
  ];

  const seed = (name || empId || "Emp").split("").reduce((acc, c) => acc + c.charCodeAt(0), 0);
  const color = colors[seed % colors.length];

  const parts = (name || "").trim().split(/\s+/);
  let initial = "?";
  if (parts.length >= 2 && parts[0] && parts[1]) {
    initial = `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  } else if (parts.length === 1 && parts[0]) {
    initial = parts[0].slice(0, 2).toUpperCase();
  } else if (empId) {
    initial = empId.slice(-2);
  }

  return { ...color, initial };
}

/**
 * Normalizes an employee record from arbitrary SAP payload
 */
export function normalizeEmployeeItem(raw: any, index: number): EmployeeCardItem {
  if (!raw || typeof raw !== "object") {
    return {
      id: `emp-${index}`,
      empId: `EMP-${index + 1}`,
      fullName: "Team Member",
      designation: "N/A",
      department: "N/A",
      email: "",
      phone: "",
      status: "Active",
      raw: { value: raw },
    };
  }

  const empId = String(
    raw.empId ||
    raw.EmpID ||
    raw.pernr ||
    raw.PERNR ||
    raw.employee_id ||
    raw.id ||
    `0000000${index + 1}`.slice(-8)
  ).trim();

  const firstName = String(raw.firstname || raw.firstName || raw.FIRSTNAME || "");
  const lastName = String(raw.lastname || raw.lastName || raw.lsastname || raw.LASTNAME || "");
  const constructedName = `${firstName} ${lastName}`.trim();

  const fullName = String(
    raw.fullName ||
    raw.fullname ||
    raw.name ||
    raw.NAME ||
    raw.cname ||
    raw.CNAME ||
    raw.ename ||
    raw.ENAME ||
    raw.employee_name ||
    constructedName ||
    `Employee #${empId}`
  );

  const designation = String(
    raw.designation ||
    raw.designation_name ||
    raw.plans_txt ||
    raw.PLANS_TXT ||
    raw.position ||
    raw.positionTitle ||
    raw.title ||
    raw.role ||
    "Officer"
  );

  const department = String(
    raw.department ||
    raw.department_name ||
    raw.orgeh_txt ||
    raw.ORGEH_TXT ||
    raw.org_unit ||
    raw.orgUnitName ||
    raw.sap_department_designation_wings?.department_name ||
    "DMRC Operations"
  );

  const wing = String(
    raw.wing ||
    raw.wing_name ||
    raw.sap_department_designation_wings?.wing_name ||
    raw.cadre ||
    ""
  );

  const email = String(
    raw.email ||
    raw.official_email ||
    raw.mail ||
    raw.usrid_long ||
    raw.USRID_LONG ||
    ""
  );

  const phone = String(
    raw.phone ||
    raw.mobile ||
    raw.contact_no ||
    raw.cellphone ||
    raw.usrid ||
    ""
  );

  const reportingManager = String(
    raw.reportingManager ||
    raw.reporting_to ||
    raw.manager_name ||
    raw.supervisor ||
    ""
  );

  const location = String(
    raw.location ||
    raw.office_location ||
    raw.work_location ||
    raw.station ||
    ""
  );

  const dateOfJoining = String(
    raw.dateOfJoining ||
    raw.doj ||
    raw.entry_date ||
    raw.date_of_joining ||
    ""
  );

  const rawStatus =
    raw.status != null
      ? raw.status
      : raw.emp_status != null
      ? raw.emp_status
      : raw.stat2 != null
      ? raw.stat2
      : "Active";
  const status = typeof rawStatus === "object" ? JSON.stringify(rawStatus) : String(rawStatus);

  const image =
    raw.image ||
    raw.photo ||
    raw.profile_picture ||
    undefined;

  const grade = String(raw.grade || raw.payScale || raw.payscale || "");
  const positionId = String(raw.positionId || raw.plans || "");
  const positionTitle = String(raw.positionTitle || raw.plans_txt || designation);
  const orgUnitId = String(raw.orgUnitID || raw.orgUnitId || raw.orgeh || "");
  const orgUnitName = String(raw.orgUnitName || raw.orgeh_txt || department);

  return {
    id: raw.id || empId || `emp-${index}`,
    empId,
    fullName,
    firstName,
    lastName,
    designation,
    department,
    wing,
    email,
    phone,
    reportingManager,
    location,
    dateOfJoining,
    status,
    image,
    grade,
    positionId,
    positionTitle,
    orgUnitId,
    orgUnitName,
    raw,
  };
}

/**
 * Normalizes an Internal Hierarchy item (e.g. nested reporting position or unit)
 */
export function normalizeInternalHierarchy(raw: any, index: number, level: number = 1): InternalHierarchyItem {
  if (!raw || typeof raw !== "object") {
    return {
      id: `hier-${level}-${index}`,
      positionTitle: "Assigned Position",
      holderName: "Vacant",
      status: "Vacant",
      level,
      raw: { value: raw },
    };
  }

  const holderEmpId = String(raw.holderEmpId || raw.empId || raw.pernr || raw.employee_id || "");
  const holderName = String(
    raw.holderName ||
    raw.name ||
    raw.fullName ||
    raw.fullname ||
    raw.cname ||
    raw.ename ||
    raw.employee_name ||
    (holderEmpId ? `Employee ${holderEmpId}` : "Vacant")
  );

  const positionId = String(raw.positionId || raw.plans || raw.position_id || "");
  const positionTitle = String(
    raw.positionTitle ||
    raw.plans_txt ||
    raw.designation ||
    raw.designation_name ||
    raw.role ||
    "Assigned Position"
  );

  const orgUnitId = String(raw.orgUnitId || raw.orgeh || raw.org_unit_id || "");
  const orgUnitName = String(raw.orgUnitName || raw.orgeh_txt || raw.department || "");
  const rawStatus = raw.status != null ? raw.status : holderEmpId ? "Occupied" : "Vacant";
  const status = typeof rawStatus === "object" ? JSON.stringify(rawStatus) : String(rawStatus);

  const rawChildren = Array.isArray(raw.internalHierarchies)
    ? raw.internalHierarchies
    : Array.isArray(raw.internal_hierarchies)
    ? raw.internal_hierarchies
    : Array.isArray(raw.internalHierarchy)
    ? raw.internalHierarchy
    : Array.isArray(raw.internal_hierarchy)
    ? raw.internal_hierarchy
    : Array.isArray(raw.children)
    ? raw.children
    : Array.isArray(raw.hierarchies)
    ? raw.hierarchies
    : Array.isArray(raw.positions)
    ? raw.positions
    : Array.isArray(raw.subUnits)
    ? raw.subUnits
    : [];

  const internalHierarchies = rawChildren.map((child: any, cIdx: number) =>
    normalizeInternalHierarchy(child, cIdx, level + 1)
  );

  return {
    id: raw.id || `${orgUnitId}-${positionId}-${holderEmpId}-${index}`,
    orgUnitId,
    orgUnitName,
    positionId,
    positionTitle,
    holderEmpId,
    holderName,
    designation: positionTitle,
    department: orgUnitName,
    status,
    reportsTo: String(raw.reportsTo || raw.reportsToPosition || ""),
    level,
    internalHierarchies: internalHierarchies.length > 0 ? internalHierarchies : undefined,
    raw,
  };
}

/**
 * Normalizes an Org Unit / Position item from arbitrary SAP payload
 */
export function normalizeOrgUnitItem(raw: any, index: number, fallbackUnitName?: string): OrgUnitPositionItem {
  if (!raw || typeof raw !== "object") {
    return {
      id: `org-${index}`,
      orgUnitId: `OU-${index + 1}`,
      orgUnitName: fallbackUnitName || "Unit",
      positionId: `POS-${index + 1}`,
      positionTitle: "Position",
      holderEmpId: "",
      holderName: "Vacant",
      status: "Vacant",
      raw: { value: raw },
    };
  }

  const orgUnitId = String(
    raw.orgUnitID ||
    raw.orgUnitId ||
    raw.orgeh ||
    raw.org_unit_id ||
    raw.unit_id ||
    raw.id ||
    `OU-${index + 1}`
  );

  // 'orgUnit' is strictly the keyword for the unit name
  const orgUnitName = String(
    raw.orgUnit ||
    raw.orgUnitName ||
    raw.org_unit ||
    raw.orgeh_txt ||
    raw.org_unit_name ||
    raw.department ||
    fallbackUnitName ||
    "Department Unit"
  );

  const positionId = String(raw.positionId || raw.plans || raw.position_id || `POS-${index + 1}`);
  const positionTitle = String(raw.positionTitle || raw.plans_txt || raw.position_name || raw.designation || "Assigned Role");
  const holderEmpId = String(raw.holderEmpId || raw.pernr || raw.empId || raw.employee_id || "");
  const holderName = String(raw.holderName || raw.cname || raw.ename || raw.name || (holderEmpId ? `Employee ${holderEmpId}` : "Vacant"));
  const reportsToPosition = String(raw.reportsToPosition || raw.parent_position || raw.manager_position || "");
  const rawStatus = raw.status != null ? raw.status : holderEmpId ? "Occupied" : "Vacant";
  const status = typeof rawStatus === "object" ? JSON.stringify(rawStatus) : String(rawStatus);

  // Extract internal hierarchies array with support for typo and casing variations
  const rawChildren =
    Array.isArray(raw.internalHierarchies) ? raw.internalHierarchies :
    Array.isArray(raw.internalHiarachies) ? raw.internalHiarachies :
    Array.isArray(raw.internal_hierarchies) ? raw.internal_hierarchies :
    Array.isArray(raw.internal_hiarachies) ? raw.internal_hiarachies :
    Array.isArray(raw.InternalHierarchies) ? raw.InternalHierarchies :
    Array.isArray(raw.InternalHiarachies) ? raw.InternalHiarachies :
    Array.isArray(raw.internalHierarchy) ? raw.internalHierarchy :
    Array.isArray(raw.internalHiarachy) ? raw.internalHiarachy :
    Array.isArray(raw.internal_hierarchy) ? raw.internal_hierarchy :
    Array.isArray(raw.internal_hiarachy) ? raw.internal_hiarachy :
    Array.isArray(raw.hierarchies) ? raw.hierarchies :
    Array.isArray(raw.hiarachies) ? raw.hiarachies :
    Array.isArray(raw.children) ? raw.children :
    Array.isArray(raw.positions) ? raw.positions :
    Array.isArray(raw.subUnits) ? raw.subUnits :
    Array.isArray(raw.sub_units) ? raw.sub_units :
    [];

  const internalHierarchies = rawChildren.map((child: any, cIdx: number) =>
    normalizeInternalHierarchy(child, cIdx, 1)
  );

  return {
    id: raw.id || `${orgUnitId}-${positionId}-${index}`,
    orgUnitId,
    orgUnitName,
    positionId,
    positionTitle,
    holderEmpId,
    holderName,
    reportsToPosition,
    status,
    internalHierarchies: internalHierarchies.length > 0 ? internalHierarchies : undefined,
    raw,
  };
}

function asText(value: unknown): string {
  if (value === null || value === undefined) return "";
  return String(value).trim();
}

/**
 * Actual API envelope:
 * { data: { success, status, message, data: { reportingLine, display, totalRecords, data: [] } } }
 */
function unwrapManagerPayload(responseData: any): { envelope: any; payload: any } {
  const envelope =
    responseData?.data &&
    typeof responseData.data === "object" &&
    !Array.isArray(responseData.data) &&
    ("success" in responseData.data || "message" in responseData.data || responseData.data?.data)
      ? responseData.data
      : responseData;

  const inner = envelope?.data;
  const payload =
    inner &&
    typeof inner === "object" &&
    !Array.isArray(inner) &&
    ("reportingLine" in inner || "totalRecords" in inner || "display" in inner || Array.isArray(inner.data))
      ? inner
      : envelope;

  return { envelope, payload };
}

export function normalizeReportingLine(raw: any): ReportingLine | null {
  if (!raw || typeof raw !== "object") return null;
  const empId = asText(raw.empId || raw.EmpID || raw.pernr);
  const name = asText(raw.name || raw.fullName || raw.ename);
  if (!empId && !name) return null;
  return {
    empId,
    name: name || empId,
    designation: asText(raw.designation || raw.positionTitle || raw.plans_txt),
    orgUnitID: asText(raw.orgUnitID || raw.orgUnitId || raw.orgeh),
    orgUnitName: asText(raw.orgUnitName || raw.orgUnit || raw.orgeh_txt),
  };
}

/**
 * Extract array of items from diverse backend response envelopes
 */
function extractResponseList(data: any, displayMode?: DisplayMode): any[] {
  if (!data) return [];
  if (Array.isArray(data)) return data;

  if (displayMode === "ORG_UNITS_AND_POSITIONS") {
    // Specifically search for 'orgUnits' array for cards
    const orgUnitsCandidates = [
      data?.Response?.data?.orgUnits,
      data?.data?.data?.orgUnits,
      data?.data?.orgUnits,
      data?.orgUnits,
      data?.Response?.data?.org_units,
      data?.data?.org_units,
      data?.org_units,
    ];
    for (const c of orgUnitsCandidates) {
      if (Array.isArray(c)) return c;
    }
  }

  // Check nested structures
  const candidates = [
    data?.Response?.data?.orgUnits,
    data?.data?.orgUnits,
    data?.orgUnits,
    data?.Response?.data?.employees,
    data?.data?.employees,
    data?.employees,
    data?.Response?.data?.data,
    data?.Response?.data?.list,
    data?.Response?.data,
    data?.data?.data?.data,
    data?.data?.data,
    data?.data?.list,
    data?.data?.positions,
    data?.data?.results,
    data?.data,
    data?.list,
    data?.positions,
    data?.results,
  ];

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) {
      return candidate;
    }
  }

  // If data is an object containing objects as map
  if (typeof data === "object") {
    const values = Object.values(data);
    if (values.length > 0 && typeof values[0] === "object") {
      return values;
    }
  }

  return [];
}

/**
 * Calls the Manager Self Service API
 */
export async function fetchManagerSelfServiceData(
  params: ManagerSelfServiceParams
): Promise<ManagerSelfServiceResult> {
  const formattedEmpId = padEmpId(params.empId);
  const endpoint = "/api/employee/manager-self-service";
  const url = API_URLS.MANAGER_SELF_SERVICE || "https://hrmsdev.dmrc.org/api/employee/manager-self-service";

  const token = tokenService.getToken();
  const authHeader = token
    ? token.startsWith("Bearer ")
      ? token
      : `Bearer ${token}`
    : undefined;

  const customHeaders = {
    device_id: "WEB",
    device_token: "WEB",
    device_type: "WEB",
    guid: typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`,
    epoch: Date.now(),
    url: endpoint,
    service_request_id: endpoint,
  };

  const requestBody = {
    Header: customHeaders,
    Request: {
      data: {
        display: params.display,
        empId: formattedEmpId,
        query: params.query ? params.query.trim() : "",
      },
    },
  };

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    "enc-req": "false",
    "enc-res": "false",
  };

  if (authHeader) {
    headers["Authorization"] = authHeader;
  }

  try {
    const response = await axios.post(url, requestBody, {
      headers,
      withCredentials: true,
      timeout: 30000,
    });

    const responseData = response.data;
    const { envelope, payload: resultBody } = unwrapManagerPayload(responseData);

    if (envelope?.success === false || resultBody?.success === false) {
      throw new Error(
        envelope?.message || resultBody?.message || "Failed to fetch manager self-service data."
      );
    }

    const reportingLine = normalizeReportingLine(resultBody?.reportingLine);
    const listFromPayload = Array.isArray(resultBody?.data) ? resultBody.data : null;
    const rawList = listFromPayload ?? extractResponseList(responseData, params.display);
    const totalCount = Number.isFinite(Number(resultBody?.totalRecords))
      ? Number(resultBody.totalRecords)
      : rawList.length;

    const topOrgUnitName = reportingLine?.orgUnitName || undefined;

    let employees: EmployeeCardItem[] = [];
    let orgUnits: OrgUnitPositionItem[] = [];

    if (params.display === "ORG_UNITS_AND_POSITIONS") {
      orgUnits = rawList.map((item: any, idx: number) => normalizeOrgUnitItem(item, idx, topOrgUnitName));
      employees = rawList.map((item: any, idx: number) => normalizeEmployeeItem(item, idx));
    } else {
      employees = rawList.map((item: any, idx: number) => normalizeEmployeeItem(item, idx));
    }

    return {
      success: true,
      message: envelope?.message || resultBody?.message,
      displayMode: (resultBody?.display as DisplayMode) || params.display,
      empId: reportingLine?.empId || formattedEmpId,
      query: params.query || "",
      totalCount,
      reportingLine,
      employees,
      orgUnits,
      rawResponse: responseData,
    };
  } catch (error: any) {
    console.error("Manager Self Service API error:", error);

    const errorMessage =
      error?.response?.data?.message ||
      error?.response?.data?.data?.message ||
      error?.message ||
      "Failed to fetch manager self-service data.";

    throw new Error(errorMessage);
  }
}
